import React, { useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faEye,
  faCheck,
  faTimes,
  faUsers,
  faUserCheck,
} from '@fortawesome/free-solid-svg-icons';
import { useRole } from '../../context/roleContext';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from '../../utils/constants';


const UsersTable = ({
  users = [],
  loading = false,
  selectedUsers = [],
  onSelectUser,
  onSelectAll,
  onEditUser,
  onDeleteUser,
  onViewUser,
  onViewWarehouses,
  onToggleStatus,
  viewLoading = false,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess, checkEditAccess, userRole } = useRole();

  /**
   * Render a status badge with icon and label based on active/inactive state.
   * Used in the users table status column.
   */
  const getStatusBadge = useCallback(
    (isActive) => (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon
          icon={isActive ? faCheck : faTimes}
          className="text-sm"
          style={{ color: isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}
        />
        <span
          className="text-sm font-medium"
          style={{ color: isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}
        >
          {isActive ? t('users.table.statuses.active') : t('users.table.statuses.inactive')}
        </span>
      </div>
    ),
    [t]
  );
  const hasUsers = Array.isArray(users) && users.length > 0;
  const selectedUsersSet = useMemo(() => new Set(selectedUsers), [selectedUsers]);
  const allSelected = hasUsers && selectedUsers.length === users.length;

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
    []
  );
  const selectedColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
    []
  );
  const selectionAccent = UI_COLORS.BUTTONS.PRIMARY.background;

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        label: t('users.table.columns.select'),
        width: '60px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderHeader: ({ loading: isLoading }) => (
          <input
            type="checkbox"
            aria-label={t('users.table.aria.selectAll')}
            checked={allSelected}
            onChange={() => onSelectAll?.(!allSelected)}
            disabled={!hasUsers || isLoading}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        renderCell: (user) => {
          const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
          const ariaLabel = fullName
            ? `${t('users.table.aria.selectUser')} ${fullName}`
            : t('users.table.aria.selectUser');

          return (
            <input
              type="checkbox"
              aria-label={ariaLabel}
              checked={selectedUsersSet.has(user.id)}
              onChange={(event) => onSelectUser?.(user.id, event.target.checked)}
              className="rounded focus:ring-2"
              style={{ accentColor: selectionAccent }}
            />
          );
        },
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'first_name',
        label: t('users.table.columns.name'),
        sortable: true,
        sortAccessor: (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim().toLowerCase(),
        renderCell: (user) => (
          <div className="flex items-center space-x-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
              }}
            >
              {user?.first_name?.[0]?.toUpperCase() || t('users.table.copy.initialFallback')}
            </div>
            <div>
              <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || t('users.table.copy.unnamedUser')}
              </div>
            </div>
          </div>
        ),
        skeleton: (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full" />
            <div className="w-24 h-4 bg-gray-300 rounded" />
          </div>
        ),
      },
      {
        id: 'email',
        label: t('users.table.columns.email'),
        sortable: true,
        sortAccessor: (user) => (user?.email || '').toLowerCase(),
        renderCell: (user) => (
          <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {user?.email || t('users.table.copy.noEmail')}
          </span>
        ),
        skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'mobile',
        label: t('users.table.columns.mobile'),
        sortable: true,
        sortAccessor: (user) =>
          (user?.mobile_number || user?.phone_number || '').toString().toLowerCase(),
        renderCell: (user) => (
          <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {user?.mobile_number || user?.phone_number || t('users.table.copy.noMobile')}
          </span>
        ),
        skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'user_type',
        label: t('users.table.columns.role'),
        sortable: true,
        sortAccessor: (user) => (user?.user_type || '').toLowerCase(),
        renderCell: (user) => {
          const roleOption = t('users.forms.userTypes', { returnObjects: true }).find(option => option.value === user?.user_type);
          return (
            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.2),
                color: UI_COLORS.TEXT.PRIMARY,
              }}
            >
              {roleOption ? roleOption.label : (user?.user_type || t('common.symbols.emDash'))}
            </span>
          );
        },
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'warehouses',
        label: t('users.table.columns.warehouses'),
        width: '150px',
        sortable: false,
        align: 'center',
        // justify: 'center',
        renderCell: (user) => {
          const count = Array.isArray(user.warehouses) ? user.warehouses.length : 0;
          const defaultWH = user.default_warehouse_name || user.default_warehouse_alias;
          
          if (count === 0) return <span className="text-gray-400">-</span>;

          return (
            <div className="flex flex-col items-center space-y-1">
              <button
                onClick={() => onViewWarehouses?.(user)}
                className="text-xs font-medium hover:underline focus:outline-none px-3 py-1 rounded-full transition-colors flex items-center"
                style={{
                  color: UI_COLORS.BRAND.SECONDARY,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5)
                }}
              >
                <FontAwesomeIcon icon={faEye} className="mr-1 text-black" />
                Preview ({count})
              </button>
              {defaultWH && (
                <span 
                  className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-tighter"
                  style={{ 
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1),
                    color: UI_COLORS.STATUS.SUCCESS,
                    border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.3)}`
                  }}
                  title="Default Warehouse"
                >
                  {defaultWH}
                </span>
              )}
            </div>
          );
        },
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'is_active',
        label: t('users.table.columns.status'),
        width: '120px',
        sortable: true,
        align: 'center',
        // justify: 'center',
        // alignItems: 'center',
        sortAccessor: (user) => (user?.is_active ? 1 : 0),
        renderCell: (user) => getStatusBadge(Boolean(user?.is_active)),
        skeleton: <div className="w-16 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'actions',
        label: t('users.table.columns.actions'),
        align: 'center',
        // justify: 'center',
        // alignItems: 'center',
        sortable: false,
        renderCell: (user) => (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => onViewUser?.(user.id)}
              disabled={viewLoading}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
              style={{
                color: UI_COLORS.TEXT.PRIMARY,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35),
              }}
              onMouseEnter={(event) => {
                if (!viewLoading) {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.NAVIGATION.HOVER,
                    0.5
                  );
                }
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.NAVIGATION.HOVER,
                  0.35
                );
              }}
              title={t('users.table.aria.viewUser')}
            >
              <FontAwesomeIcon icon={faEye} className="text-sm" />
            </button>
            {checkEditAccess(null) &&
              !(
                userRole === USER_ROLES.ADMIN &&
                (user.user_type === USER_ROLES.ADMIN || user.user_type === USER_ROLES.SUPER_ADMIN)
              ) && (
                <button
                  onClick={() => onEditUser?.(user.id)}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.WARNING,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.1
                    );
                  }}
                  title={t('users.table.aria.editUser')}
                >
                  <FontAwesomeIcon icon={faEdit} className="text-sm" />
                </button>
              )}
            {/* Deleting a user here is a SOFT delete — it sets is_active = false and
                leaves the row in place — so this is the only way back. Super Admin
                only, matching the endpoint, and shown only on a user who is actually
                deactivated; for an active user the delete button IS the deactivate. */}
            {userRole === USER_ROLES.SUPER_ADMIN && !user.is_active && (
              <button
                onClick={() => onToggleStatus?.(user.id, true)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.SUCCESS,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.SUCCESS,
                    0.2
                  );
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.SUCCESS,
                    0.1
                  );
                }}
                title={t('users.table.aria.activateUser')}
              >
                <FontAwesomeIcon icon={faUserCheck} className="text-sm" />
              </button>
            )}
            {checkDeleteAccess() &&
              !(
                userRole === USER_ROLES.ADMIN &&
                (user.user_type === USER_ROLES.ADMIN || user.user_type === USER_ROLES.SUPER_ADMIN)
              ) && (
                <button
                  onClick={() => onDeleteUser?.(user.id)}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.ERROR,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.1
                    );
                  }}
                  title={t('users.table.aria.deleteUser')}
                >
                  <FontAwesomeIcon icon={faTrash} className="text-sm" />
                </button>
              )}
          </div>
        ),
        skeleton: (
          <div className="flex items-center justify-center space-x-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-8 h-8 bg-gray-300 rounded-lg" />
            ))}
          </div>
        ),
      },
    ],
    [
      allSelected,
      hasUsers,
      onDeleteUser,
      onEditUser,
      onSelectAll,
      onSelectUser,
      onToggleStatus,
      onViewUser,
      onViewWarehouses,
      selectedUsersSet,
      selectionAccent,
      viewLoading,
      checkDeleteAccess,
      checkEditAccess,
      userRole,
      t,
      getStatusBadge,
    ]
  );

  /**
   * Row property builder for table rows.
   * - Applies hover/selected background styles and event handlers.
   */
  const getRowProps = useCallback(
    (user) => {
      const isSelected = selectedUsersSet.has(user.id);
      return {
        className: 'hover:bg-opacity-50 transition-colors',
        style: {
          backgroundColor: isSelected ? selectedColor : 'transparent',
        },
        onMouseEnter: (event) => {
          if (!isSelected) {
            event.currentTarget.style.backgroundColor = hoverColor;
          }
        },
        onMouseLeave: (event) => {
          event.currentTarget.style.backgroundColor = isSelected ? selectedColor : 'transparent';
        },
      };
    },
    [hoverColor, selectedColor, selectedUsersSet]
  );

  const emptyState = useMemo(
    () => ({
      icon: faUsers,
      title: t('users.table.emptyState.title'),
      message: t('users.table.emptyState.message'),
    }),
    [t]
  );

  return (
    <DataTable
      columns={columns}
      data={Array.isArray(users) ? users : []}
      loading={loading}
      keyField="id"
      enablePagination={false}
      defaultSort={{ field: 'first_name', direction: 'asc' }}
      getRowProps={getRowProps}
      emptyState={emptyState}
    />
  );
};

export default UsersTable;